import { useBillingSubscriptionPricesQuery } from '@graphql/queries/billing-subscription/price.graphql';

import { getCurrencyCodeByCountry, CURRENCY_CODE_SYMBOL_MAP } from './currency-map';
import { BillingSubscriptionPriceDetailsFragment } from '@graphql/fragments/billing-subscription/price.graphql';
import { BillingSubscriptionProductDetailsFragment } from '@graphql/fragments/billing-subscription/product.graphql';
import { useMemo } from 'react';

interface UsePrices {
  /**
   * Blinq billing-subscription prices.
   */
  prices: BillingSubscriptionPriceDetailsFragment[];
  /**
   * Blinq billing-subscription products.
   */
  products: BillingSubscriptionProductDetailsFragment[];
  /**
   * The current user's currency.
   */
  currency: string;
  loading: boolean;
}

function usePrices(): UsePrices {
  const { data, loading } = useBillingSubscriptionPricesQuery();

  const prices = useMemo(() => {
    return (data?.billingSubscriptionPrices ?? [])
      .filter((price) => price.product?.provider === 'stripe')
      .filter((price) => price.product?.is_current)
      .filter((price): price is BillingSubscriptionPriceDetailsFragment => price.is_current === true);
  }, [data?.billingSubscriptionPrices]);

  const products = useMemo(() => {
    return prices
      .map((price) => price.product)
      .filter((product): product is BillingSubscriptionProductDetailsFragment => !!product);
  }, [prices]);

  const currency = useMemo(() => {
    const countryCode = getCurrencyCodeByCountry({
      countryCode: 'US',
      defaultCurrencyCode: 'USD',
      whitelist: ['EUR', 'USD', 'AUD', 'CAD', 'GBP'],
    });

    return CURRENCY_CODE_SYMBOL_MAP[countryCode];
  }, []);

  return {
    prices,
    products,
    currency,
    loading,
  };
}

export { usePrices };
